import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import {
  mapIntegrationLinksListFromDTO,
  mergeIntegrations,
} from '@/modules/common/mappers/integration.mapper';
import type {
  IntegrationInfoResponse,
  IntegrationLinksResponse,
} from '@/modules/common/types/dto/response/integration-response';
import { EntityType } from '@/modules/common/types/entity-type';
import { axiosInstance } from '@/utils/axios';

import {
  mapClientIntegrationsToPatchRequest,
  mapEntityIntegrationsToClientIntegrations,
} from '../mappers/client-integration.mapper';
import { ClientIntegration } from '../types/client';

/**
 * Retrieves all available integrations with their unlinked external clients.
 * Optionally merges with selected integrations from client details.
 *
 * @param selectedIntegrations - Optional integration data from client details with selected external clients
 * @returns Integrations with complete client lists and selections (if provided)
 */
export const getAvailableIntegrations = async (
  selectedIntegrations?: IntegrationInfoResponse[]
): Promise<ClientIntegration[]> => {
  const endpoint = '/api/entity-integration-links/unlinked';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.INTEGRATIONS_LOAD_FAILED, () =>
    axiosInstance.get<{
      data: IntegrationLinksResponse[];
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      params: {
        entityType: EntityType.Client,
      },
    })
  );

  const unlinkedIntegrations = mapIntegrationLinksListFromDTO(response);

  const finalIntegrations = selectedIntegrations
    ? mergeIntegrations(unlinkedIntegrations, selectedIntegrations)
    : unlinkedIntegrations;

  return mapEntityIntegrationsToClientIntegrations(finalIntegrations).sort((a, b) =>
    a.type.localeCompare(b.type)
  );
};

/**
 * Updates client integrations only.
 * Uses dedicated integrations endpoint.
 * PATCH /api/clients/integrations
 *
 * @param clientId - Client ID to update integrations for
 * @param integrations - Array of client integrations with selections
 */
export const patchClientIntegrations = async (
  clientId: string | number,
  integrations?: ClientIntegration[]
): Promise<void> => {
  const endpoint = '/api/clients/integrations';
  const patchRequest = mapClientIntegrationsToPatchRequest(clientId, integrations);

  await withApiErrorHandler(endpoint, ApiErrorCode.CLIENT_UPDATE_FAILED, () =>
    axiosInstance.patch<{
      data: Record<string, never>;
      error?: { status: number; message: string; code: string };
    }>(endpoint, patchRequest)
  );
};
